import { jsPDF } from 'jspdf';
import type { DiagramState } from '../types/hld';

export interface HLDWorkspacePdfData {
  title: string;
  statement: string;
  difficulty: string;
  estimatedTimeMinutes: number;
  functionalRequirements: string[];
  nonFunctionalRequirements: string[];
  addressedRequirements: string[];
  assumptions: string[];
  notes: string;
  tradeoffs: string;
  diagram: DiagramState;
  score: number | null;
}

const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN = 42;
const CONTENT_W = PAGE_W - MARGIN * 2;
const PAGE_BOTTOM = PAGE_H - 46;

const safeFilename = (value: string) => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '') || 'hld-design';

/** Generate a concise, print-ready HLD design report in the browser. */
export function generateHLDWorkspacePdf(data: HLDWorkspacePdfData): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  let y = MARGIN;

  const setInk = (shade: number) => doc.setTextColor(shade, shade + 7, shade + 3);
  const addPage = () => {
    doc.addPage();
    y = MARGIN;
    header();
  };
  const ensure = (height: number) => {
    if (y + height > PAGE_BOTTOM) addPage();
  };
  const header = () => {
    doc.setFillColor(5, 93, 67);
    doc.rect(0, 0, PAGE_W, 28, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text('LETSBOOSTUP / HLD DESIGN PRACTICE', MARGIN, 18);
    doc.setFont('helvetica', 'normal');
    doc.text(data.title, PAGE_W - MARGIN, 18, { align: 'right' });
    y = 48;
  };
  const paragraph = (text: string, size = 10, color = 62) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(size);
    setInk(color);
    const lines = doc.splitTextToSize(text || '—', CONTENT_W) as string[];
    lines.forEach((line) => { ensure(size * 1.55); doc.text(line, MARGIN, y); y += size * 1.55; });
  };
  const section = (title: string) => {
    ensure(48);
    y += 12;
    doc.setDrawColor(190, 220, 208);
    doc.setLineWidth(1);
    doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
    y += 18;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(5, 93, 67);
    doc.text(title, MARGIN, y);
    y += 15;
  };
  const checklist = (items: string[]) => {
    if (items.length === 0) { paragraph('No items recorded.', 9.5, 115); return; }
    items.forEach((item) => {
      const addressed = data.addressedRequirements.includes(item);
      doc.setFont('helvetica', addressed ? 'bold' : 'normal');
      doc.setFontSize(9.5);
      const lines = doc.splitTextToSize(item, CONTENT_W - 24) as string[];
      ensure(Math.max(18, lines.length * 14));
      doc.setFillColor(addressed ? 5 : 231, addressed ? 150 : 238, addressed ? 105 : 234);
      doc.roundedRect(MARGIN, y - 9, 10, 10, 2, 2, 'F');
      if (addressed) {
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.text('✓', MARGIN + 5, y - 1.5, { align: 'center' });
      }
      setInk(55);
      lines.forEach((line, index) => doc.text(line, MARGIN + 18, y + index * 14));
      y += Math.max(18, lines.length * 14);
    });
  };
  const drawDiagram = () => {
    const { nodes = [], edges = [] } = data.diagram;
    const diagramHeight = 238;
    ensure(diagramHeight + 20);
    const top = y;
    doc.setFillColor(248, 251, 249);
    doc.setDrawColor(196, 216, 207);
    doc.roundedRect(MARGIN, top, CONTENT_W, diagramHeight, 8, 8, 'FD');
    if (nodes.length === 0) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(116, 132, 124);
      doc.text('No architecture components have been added yet.', MARGIN + CONTENT_W / 2, top + diagramHeight / 2, { align: 'center' });
      y += diagramHeight;
      return;
    }
    const xs = nodes.map((node) => node.position.x);
    const ys = nodes.map((node) => node.position.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const boxW = Math.min(112, Math.max(74, (CONTENT_W - 48) / Math.max(2, Math.ceil(Math.sqrt(nodes.length)))));
    const boxH = 38;
    const positionFor = (node: typeof nodes[number]) => ({
      x: MARGIN + 24 + ((node.position.x - minX) / Math.max(maxX - minX, 1)) * (CONTENT_W - boxW - 48),
      y: top + 24 + ((node.position.y - minY) / Math.max(maxY - minY, 1)) * (diagramHeight - boxH - 48),
    });
    const positions = new Map(nodes.map((node) => [node.id, positionFor(node)]));
    doc.setDrawColor(93, 126, 111);
    doc.setLineWidth(1);
    edges.forEach((edge) => {
      const from = positions.get(edge.source);
      const to = positions.get(edge.target);
      if (!from || !to) return;
      const fromX = from.x + boxW / 2;
      const fromY = from.y + boxH / 2;
      const toX = to.x + boxW / 2;
      const toY = to.y + boxH / 2;
      doc.line(fromX, fromY, toX, toY);
      const angle = Math.atan2(toY - fromY, toX - fromX);
      const arrow = 5;
      doc.line(toX, toY, toX - arrow * Math.cos(angle - Math.PI / 6), toY - arrow * Math.sin(angle - Math.PI / 6));
      doc.line(toX, toY, toX - arrow * Math.cos(angle + Math.PI / 6), toY - arrow * Math.sin(angle + Math.PI / 6));
      if (edge.label) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(60, 85, 73);
        doc.text(edge.label, (fromX + toX) / 2, (fromY + toY) / 2 - 4, { align: 'center' });
      }
    });
    nodes.forEach((node) => {
      const position = positions.get(node.id)!;
      doc.setFillColor(229, 244, 236);
      doc.setDrawColor(5, 120, 87);
      doc.roundedRect(position.x, position.y, boxW, boxH, 5, 5, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(5, 93, 67);
      doc.text(node.type.toUpperCase(), position.x + 7, position.y + 11);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(30, 48, 39);
      const label = doc.splitTextToSize(node.label, boxW - 14) as string[];
      doc.text(label.slice(0, 2), position.x + 7, position.y + 23);
    });
    y += diagramHeight;
  };

  doc.setFillColor(5, 93, 67);
  doc.rect(0, 0, PAGE_W, PAGE_H, 'F');
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(MARGIN, 92, CONTENT_W, 310, 14, 14, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(110, 126, 117);
  doc.text('HLD DESIGN REPORT', MARGIN + 28, 135);
  doc.setFontSize(28);
  doc.setTextColor(20, 36, 29);
  const titleLines = doc.splitTextToSize(data.title, CONTENT_W - 56) as string[];
  doc.text(titleLines, MARGIN + 28, 178);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(68, 82, 74);
  const statementLines = doc.splitTextToSize(data.statement, CONTENT_W - 56) as string[];
  doc.text(statementLines.slice(0, 5), MARGIN + 28, 228);
  doc.setFillColor(231, 247, 239);
  doc.roundedRect(MARGIN + 28, 330, CONTENT_W - 56, 42, 8, 8, 'F');
  doc.setFontSize(9);
  doc.setTextColor(5, 93, 67);
  const metadata = `${data.difficulty} · ${data.estimatedTimeMinutes} min${data.score === null ? '' : ` · Latest review ${data.score}%`}`;
  doc.text(metadata, MARGIN + 42, 355);
  doc.setFontSize(8.5);
  doc.setTextColor(194, 220, 206);
  doc.text(`Generated ${new Date().toLocaleString()}`, MARGIN + 28, 445);

  addPage();
  section('Design brief');
  paragraph(data.statement);
  section('Requirements');
  checklist(data.functionalRequirements);
  if (data.nonFunctionalRequirements.length) {
    y += 7;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(5, 93, 67);
    doc.text('SYSTEM CONSTRAINTS', MARGIN, y);
    y += 15;
    checklist(data.nonFunctionalRequirements);
  }
  if (data.assumptions.length) {
    section('Starting assumptions');
    data.assumptions.forEach((assumption) => paragraph(`• ${assumption}`, 9.5));
  }
  section('Architecture diagram');
  drawDiagram();
  section('Notes and estimates');
  paragraph(data.notes || 'No notes recorded.', 10);
  section('Trade-offs');
  paragraph(data.tradeoffs || 'No trade-offs recorded.', 10);
  if (data.score !== null) {
    section('Review result');
    paragraph(`Latest deterministic rubric score: ${data.score}%`, 11, 5);
  }

  doc.save(`hld-${safeFilename(data.title)}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
